import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Stack
} from '@mui/material';
import {
  QueryStats,
  Refresh,
  ExpandMore,
  ExpandLess,
  TableChart,
} from '@mui/icons-material';
import api from '../../services/api';
import { useEntityContext } from '../EntityContextSwitch';

function DatasetTableRow({ table, expandedTable, onExpand, formatDate }) {
  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{ cursor: 'pointer' }}
        onClick={() => onExpand(table.tableId)}
      >
        <TableCell>
          <IconButton size="small">
            {expandedTable === table.tableId ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TableChart fontSize="small" color="action" />
            <Typography variant="body2">{table.tableId}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          {table.numRows !== undefined ? table.numRows.toLocaleString() : 'N/A'}
        </TableCell>
        <TableCell align="right">
          {table.numBytesFormatted || 'N/A'}
        </TableCell>
        <TableCell>
          <Chip
            label={table.type || 'TABLE'}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          {formatDate(table.lastModified)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={expandedTable === table.tableId} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 4 }}>
              <Typography variant="subtitle2" gutterBottom>
                Table Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="body2">{formatDate(table.created)}</Typography>
                    </Box>
                    {table.description && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Description</Typography>
                        <Typography variant="body2">{table.description}</Typography>
                      </Box>
                    )}
                    {table.timePartitioning && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">Partitioning</Typography>
                        <Typography variant="body2">
                          {table.timePartitioning.type} on {table.timePartitioning.field}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    Schema ({table.schema?.length || 0} fields)
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                    {table.schema?.map((field, index) => (
                      <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {field.name}: {field.type} {field.mode === 'REQUIRED' && '(required)'}
                      </Typography>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

// eslint-disable-next-line max-lines-per-function
function BigQueryDetailView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const { currentEntity } = useEntityContext();

  const clientId = currentEntity?.id;
  const [expandedTable, setExpandedTable] = useState(null);

  const fetchDatasetInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEntityBigQueryInfo(clientId);
      console.log('BigQuery API Response:', data);
      console.log('Tables data:', data?.tables);
      console.log('First table details:', data?.tables?.[0]);
      setDatasetInfo(data);
    } catch (err) {
      console.error('Error fetching BigQuery info:', err);
      setError(err.message || 'Failed to load BigQuery information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchDatasetInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(parseInt(dateString)).toLocaleString();
  };

  const handleExpandTable = (tableId) => {
    setExpandedTable(expandedTable === tableId ? null : tableId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!datasetInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No BigQuery dataset information available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QueryStats color="primary" />
            {datasetInfo.datasetId}
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchDatasetInfo} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Dataset Summary */}
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Tables
                </Typography>
                <Typography variant="h5">
                  {datasetInfo.tableCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Rows
                </Typography>
                <Typography variant="h5">
                  {datasetInfo.totalRows !== undefined ? datasetInfo.totalRows.toLocaleString() : '0'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Total Size
                </Typography>
                <Typography variant="h5">
                  {datasetInfo.totalBytesFormatted}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Location
                </Typography>
                <Typography variant="h6">
                  {datasetInfo.location}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      {/* Tables List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Tables ({datasetInfo.tableCount})
        </Typography>

        {datasetInfo.tableCount === 0 ? (
          <Alert severity="info">
            This dataset is empty. No tables have been created yet.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40}></TableCell>
                  <TableCell>Table Name</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Last Modified</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datasetInfo.tables.map((table) => (
                  <DatasetTableRow
                    key={table.tableId}
                    table={table}
                    expandedTable={expandedTable}
                    onExpand={handleExpandTable}
                    formatDate={formatDate}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      {/* Metadata */}
      <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Dataset Metadata
        </Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{formatDate(datasetInfo.created)}</Typography>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Typography variant="caption" color="text.secondary">Last Modified</Typography>
            <Typography variant="body2">{formatDate(datasetInfo.lastModified)}</Typography>
          </Grid>
          {datasetInfo.description && (
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 6
              }}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Typography variant="body2">{datasetInfo.description}</Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default BigQueryDetailView;